let currentWorkKey: string | null = null;
const decodedDurations = new WeakMap<object, number>();
const carriedWorkKeys = new WeakMap<object, string>();
type ProjectIdentity = {
  get: () => string | null | undefined; remember: (key: string) => void;
  projectId?: () => string | null | undefined;
  /** Changes when the editor is replaced, but not when its first save assigns an ID. */
  sessionId?: () => string | number;
};
let projectIdentity: ProjectIdentity | undefined;

/** ProjectStore supplies this without making telemetry import the editor. */
export function configureProjectWorkIdentity(identity: ProjectIdentity): void { projectIdentity = identity; }
export function getCurrentWorkProjectId(): string | null { return projectIdentity?.projectId?.() ?? null; }

/** ID changes caused by saving/adopting/rolling back within the same editor session. */
export function canFollowProjectId(previous: string | null, current: string | null): boolean {
  const cloudId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return previous === current || previous === null
    || (!cloudId.test(previous) && (current === null || cloudId.test(current)));
}

/** Bind asynchronous decoding/hashing to its editor session and file generation. */
export function beginWorkKeyCapture(isCurrentFile: () => boolean = () => true) {
  const identity = projectIdentity;
  const sessionId = identity?.sessionId?.();
  let projectId = getCurrentWorkProjectId();
  const isCurrent = () => {
    if (!isCurrentFile() || identity !== projectIdentity || sessionId !== identity?.sessionId?.()) return false;
    const currentId = getCurrentWorkProjectId();
    // First save, local-to-cloud adoption and its rollback keep the session.
    // Bind the new ID so later project switches are still rejected during hashing.
    if (canFollowProjectId(projectId, currentId)) projectId = currentId;
    return projectId === currentId;
  };
  return {
    isCurrent,
    async commit(file: { name: string; size?: number; durationSec?: number }): Promise<string | null> {
      if (!isCurrent()) return null;
      const key = await computeWorkKey(file);
      return isCurrent() ? setCurrentWorkKey(key) : null;
    },
  };
}
/** Explicit provenance only: newly uploaded files must still hash their own metadata. */
export function getCarriedWorkKey(file: object): string | undefined { return carriedWorkKeys.get(file); }
export function carryWorkKey<T extends object>(file: T, workKey?: string | null): T {
  if (workKey && /^[0-9a-f]{8,64}$/.test(workKey)) carriedWorkKeys.set(file, workKey);
  return file;
}
export function getWorkDuration(file: object): number | undefined { return decodedDurations.get(file); }
export function rememberWorkDuration(file: object, durationSec: number): void {
  if (Number.isFinite(durationSec) && durationSec >= 0) decodedDurations.set(file, durationSec);
}

/** Reuse explicit input provenance, otherwise hash metadata locally. */
export async function computeWorkKey(input: { name: string; size?: number; durationSec?: number }): Promise<string> {
  const carried = getCarriedWorkKey(input);
  if (carried) return carried;
  const duration = input.durationSec ?? decodedDurations.get(input) ?? 0;
  return hashWorkIdentity(`${input.name}|${input.size ?? 0}|${Math.round(duration)}`);
}

async function hashWorkIdentity(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  try {
    if (globalThis.crypto?.subtle) {
      const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('').slice(0, 32);
    }
  } catch { /* Restricted WebCrypto must not prevent loading audio. */ }
  let hash = 0x811c9dc5;
  for (const byte of bytes) hash = Math.imul(hash ^ byte, 0x01000193) >>> 0;
  return hash.toString(16).padStart(8, '0').repeat(4);
}

// Deliberately in memory: reopening an empty editor must not reuse yesterday's song.
export function setCurrentWorkKey(key: string | null, rememberInProject = true): string | null {
  currentWorkKey = key;
  try {
    if (key && rememberInProject) projectIdentity?.remember(key);
  } catch { /* A full or unavailable project store must not prevent loading audio. */ }
  return key;
}
export function getCurrentWorkKey(): string | null {
  if (currentWorkKey) return currentWorkKey;
  try { return projectIdentity?.get() ?? null; }
  catch { return null; }
}

/**
 * Work identity follows the input file's metadata hash; projects remember it as lastWorkKey.
 * Saved-project exports prefer lastWorkKey, falling back to the hash of currentProjectId.
 * Unsaved exports without lastWorkKey use the current in-memory input key.
 */
export async function getExportWorkKey(projectId: string | null, lastWorkKey: string | null): Promise<string | null> {
  return lastWorkKey ?? (projectId ? hashWorkIdentity(projectId) : getCurrentWorkKey());
}
